import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { CATEGORIAS, COR_CATEGORIA, invalidarAusencias, type Tipo } from './tipos';

/** Tipos de ausência: configuração do workspace (só quem vê todas as empresas cria e altera). */
export function Tipos() {
  const { t } = useTranslation('ausencias');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const pode = podeNoWorkspace(s, 'ausencias.configurar');
  const [aberto, setAberto] = useState<Tipo | 'novo' | null>(null);
  const [remover, setRemover] = useState<Tipo | null>(null);
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/ausencias/tipos/${id}`),
    onSuccess: async () => {
      setRemover(null);
      setAberto(null);
      await invalidarAusencias(qc);
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  const colunas: Coluna<Tipo>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), principal: true, render: (x) => <span className="font-medium">{x.nome}</span> },
    { chave: 'categoria', rotulo: t('campos.categoria'), render: (x) => <Selo cor={COR_CATEGORIA[x.categoria]}>{t(`categoria.${x.categoria}`)}</Selo> },
    { chave: 'saldo_anual_dias', rotulo: t('campos.saldoAnual'), alinhar: 'direita', mono: true, render: (x) => (x.saldo_anual_dias === null ? t('semLimite') : x.saldo_anual_dias) },
    { chave: 'contagem', rotulo: t('campos.contagem'), render: (x) => t(`contagem.${x.contagem}`) },
    { chave: 'remunerada', rotulo: t('campos.remunerada'), render: (x) => (x.remunerada ? t('sim') : t('nao')) },
    { chave: 'exige_aprovacao', rotulo: t('campos.exigeAprovacao'), render: (x) => (x.exige_aprovacao ? t('sim') : t('nao')) },
    { chave: 'pedidos', rotulo: t('campos.pedidos'), alinhar: 'direita', mono: true },
    { chave: 'ativo', rotulo: t('campos.ativo'), render: (x) => <Selo cor={x.ativo ? 'verde' : 'neutro'}>{x.ativo ? t('sim') : t('nao')}</Selo> },
  ];
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'categoria', rotulo: t('campos.categoria'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: CATEGORIAS.map((c) => ({ valor: c, rotulo: t(`categoria.${c}`) })) },
    { nome: 'contagem', rotulo: t('campos.contagem'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: (['corridos', 'uteis'] as const).map((c) => ({ valor: c, rotulo: t(`contagem.${c}`) })) },
    { nome: 'saldo_anual_dias', rotulo: t('campos.saldoAnual'), tipo: 'numero', min: 0, max: 366, passo: 1, largura: 'meia', ajuda: t('campos.saldoAnualAjuda') },
    { nome: 'remunerada', rotulo: t('campos.remunerada'), tipo: 'booleano', largura: 'meia' },
    { nome: 'exige_aprovacao', rotulo: t('campos.exigeAprovacao'), tipo: 'booleano', largura: 'meia', ajuda: t('campos.exigeAprovacaoAjuda') },
    { nome: 'ativo', rotulo: t('campos.ativo'), tipo: 'booleano', largura: 'meia' },
  ];
  return (
    <>
      {pode && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoTipo')}
          </Botao>
        </AcaoPrimaria>
      )}
      {!pode && s.pode('ausencias.configurar') && <p className="mb-3 text-sm text-tinta-3">{t('aviso.tiposTodas')}</p>}
      <Tabela<Tipo> tela="ausencias.tipos" caminho="/ausencias/tipos" chaveConsulta={['ausencias', 'tipos']} colunas={colunas} aoClicar={pode ? (x) => setAberto(x) : undefined} vazio={<Vazio texto={t('vazio.tipos')} />} />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? atual.nome : t('acoes.novoTipo')}
        rodape={
          <>
            {atual && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setRemover(atual)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-tipo-ausencia">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {aberto && (
          <Formulario
            key={atual?.id ?? 'novo'}
            id="form-tipo-ausencia"
            campos={campos}
            inicial={atual ? { ...atual } : { categoria: 'ferias', contagem: 'corridos', remunerada: true, exige_aprovacao: true, ativo: true }}
            aoEnviar={async (v) => {
              const saldo = v.saldo_anual_dias === '' || v.saldo_anual_dias === null || v.saldo_anual_dias === undefined ? null : Number(v.saldo_anual_dias);
              const corpo = { nome: v.nome, categoria: v.categoria, contagem: v.contagem, saldo_anual_dias: saldo, remunerada: !!v.remunerada, exige_aprovacao: !!v.exige_aprovacao, ativo: !!v.ativo };
              if (atual) await patch(`/ausencias/tipos/${atual.id}`, corpo);
              else await post('/ausencias/tipos', corpo);
              await invalidarAusencias(qc);
              toast.sucesso(tn('comum.salvo'));
              setAberto(null);
            }}
          />
        )}
      </Drawer>
      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.excluirTipo')} texto={t('confirmar.excluirTipoTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}
